import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "~/api/errors";
import { useApi } from "~/api/provider";
import type { ReasoningApi, ReasoningSession } from "~/api/reasoningTypes";
import { sessionSnapshotKey } from "./candidateDraft";
import { latestCandidate } from "./reasoningSelectors";
import {
  clearReasoningRecovery,
  criterionReviewKey,
  readReasoningRecovery,
  saveReasoningRecovery,
} from "./recoveryStorage";

type ReviewCommand = Parameters<ReasoningApi["evaluate"]>[0];
type ReviewDraft = {
  snapshot: string;
  verdicts: Array<{ criterionId: string; verdict: string; rationale: string }>;
  rationale: string;
  pending: ReviewCommand | null;
  saved: boolean;
};
function readDraft(session: ReasoningSession): ReviewDraft | null {
  try {
    const raw = readReasoningRecovery(criterionReviewKey(session.id));
    if (!raw) return null;
    const draft = JSON.parse(raw) as ReviewDraft;
    if (
      !Array.isArray(draft.verdicts) ||
      typeof draft.snapshot !== "string" ||
      typeof draft.rationale !== "string" ||
      typeof draft.saved !== "boolean"
    )
      return null;
    if (
      draft.pending &&
      (draft.pending.sessionId !== session.id ||
        !draft.pending.commandId ||
        !draft.pending.candidateRecordId)
    )
      return null;
    return draft;
  } catch {
    return null;
  }
}

/** A reviewer explicitly judges each criterion for one exact retained candidate. */
export function ReasoningCriterionReview({
  session,
  disabled,
  onSessionChange,
  onDirtyChange,
}: {
  session: ReasoningSession;
  disabled?: boolean;
  onSessionChange: (session: ReasoningSession) => void;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const { t } = useTranslation();
  const { reasoning } = useApi();
  const [initial] = useState(() => readDraft(session));
  const [base, setBase] = useState(session);
  const [snapshot, setSnapshot] = useState(initial?.snapshot ?? sessionSnapshotKey(session));
  const [verdicts, setVerdicts] = useState(
    initial?.verdicts ??
      session.criteria.map((c) => ({ criterionId: c.id, verdict: "unknown", rationale: "" })),
  );
  const [rationale, setRationale] = useState(initial?.rationale ?? "");
  const [pending, setPending] = useState<ReviewCommand | null>(initial?.pending ?? null);
  const [saved, setSaved] = useState(initial?.saved ?? false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const flight = useRef(false);
  const candidate = latestCandidate(base);
  const drifted = snapshot !== sessionSnapshotKey(session);
  const dirty =
    !!rationale || verdicts.some((v) => !!v.rationale || v.verdict !== "unknown") || !!pending;
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);
  useEffect(() => {
    const key = criterionReviewKey(base.id);
    if (dirty || saved)
      saveReasoningRecovery(
        key,
        JSON.stringify({ snapshot, verdicts, rationale, pending, saved } satisfies ReviewDraft),
      );
    else clearReasoningRecovery(key);
  }, [base.id, snapshot, verdicts, rationale, pending, saved, dirty]);
  function reset(next: ReasoningSession) {
    setBase(next);
    setSnapshot(sessionSnapshotKey(next));
    setVerdicts(
      next.criteria.map((c) => ({ criterionId: c.id, verdict: "unknown", rationale: "" })),
    );
    setRationale("");
    setPending(null);
    setSaved(false);
    setError("");
  }
  async function submit() {
    if (flight.current || disabled || !candidate?.eligible || (!pending && drifted)) return;
    flight.current = true;
    setBusy(true);
    setError("");
    const command = pending ?? {
      commandId: crypto.randomUUID(),
      sessionId: base.id,
      expectedSessionRevision: base.revision,
      candidateRecordId: candidate.id,
      contextRecordId: base.contextRecordId,
      criteriaDigest: base.criteriaDigest,
      verdicts,
      rationale,
    };
    saveReasoningRecovery(
      criterionReviewKey(base.id),
      JSON.stringify({
        snapshot,
        verdicts,
        rationale,
        pending: command,
        saved,
      } satisfies ReviewDraft),
    );
    setPending(command);
    try {
      if (!saved) {
        await reasoning.evaluate(command);
        setSaved(true);
        saveReasoningRecovery(
          criterionReviewKey(base.id),
          JSON.stringify({
            snapshot,
            verdicts,
            rationale,
            pending: command,
            saved: true,
          } satisfies ReviewDraft),
        );
      }
      const latest = (await reasoning.get(base.id)).session;
      reset(latest);
      onSessionChange(latest);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      if (
        e instanceof ApiError &&
        e.status >= 400 &&
        e.status < 500 &&
        e.status !== 408 &&
        e.status !== 429
      )
        setPending(null);
    } finally {
      flight.current = false;
      setBusy(false);
    }
  }
  if (!candidate) return null;
  return (
    <section
      className="flex flex-col gap-3 rounded border border-divider p-3"
      aria-label={t("Criterion review")}
    >
      <h4 className="font-semibold">{t("Review candidate against criteria")}</h4>
      <p>
        {t("{{name}} revision {{revision}}.", {
          name: candidate.logicalId,
          revision: candidate.revision,
        })}{" "}
        {t("Record your verdict and supporting evidence for every criterion.")}
      </p>
      {error && <p role="alert">{error}</p>}
      {drifted && (
        <p role="alert">
          {t(
            "The session changed. Keep this draft for comparison or load the current candidate before submitting.",
          )}
        </p>
      )}
      {drifted && (
        <button type="button" disabled={busy || !!pending} onClick={() => reset(session)}>
          {t("Discard review draft and load current candidate")}
        </button>
      )}
      <fieldset disabled={disabled || busy || !!pending || drifted} className="flex flex-col gap-3">
        {base.criteria.map((criterion, index) => (
          <label key={criterion.id} className="flex flex-col gap-1">
            {criterion.description} {criterion.hard ? t("(required)") : t("(advisory)")}
            <select
              aria-label={t("Verdict for {{criterion}}", { criterion: criterion.id })}
              value={verdicts[index]?.verdict ?? "unknown"}
              onChange={(e) =>
                setVerdicts((rows) =>
                  rows.map((row, i) => (i === index ? { ...row, verdict: e.target.value } : row)),
                )
              }
            >
              <option value="unknown">{t("Unknown")}</option>
              <option value="pass">{t("Pass")}</option>
              <option value="fail">{t("Fail")}</option>
            </select>
            <textarea
              aria-label={t("Evidence for {{criterion}}", { criterion: criterion.id })}
              value={verdicts[index]?.rationale ?? ""}
              onChange={(e) =>
                setVerdicts((rows) =>
                  rows.map((row, i) => (i === index ? { ...row, rationale: e.target.value } : row)),
                )
              }
            />
          </label>
        ))}
        <label className="flex flex-col">
          {t("Review summary")}
          <textarea value={rationale} onChange={(e) => setRationale(e.target.value)} />
        </label>
      </fieldset>
      <button
        type="button"
        disabled={
          disabled ||
          busy ||
          !candidate.eligible ||
          (!pending && (drifted || !rationale.trim() || verdicts.some((v) => !v.rationale.trim())))
        }
        onClick={() => void submit()}
      >
        {saved
          ? t("Reload saved evaluation")
          : pending
            ? t("Retry same evaluation")
            : t("Save criterion evaluation")}
      </button>
    </section>
  );
}
